import {
  Card,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Box,
  Typography,
  IconButton,
  Paper,
  Divider,
  Chip,
} from "@mui/material";
import WDBox from "../../../../MaterialComponents/WDBox";
import WDButton from "../../../../MaterialComponents/WDButton";
import WDTypography from "../../../../MaterialComponents/WDTypography";
import AppService from "../../../../Configurations/Appservice";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

import { Formik, Form, Field } from "formik";
import { object, string } from "yup";
import { useState, useEffect, useRef } from "react";

import { useDispatch } from "react-redux";
import {
  showLoader,
  dismissLoader,
  showSuccessMessage,
  showErroMessage,
} from "../../../../LayoutContainers/AppLayout/redux/AppSlice";
import { updateScreenTitle } from "../../../../LayoutContainers/AppLayout/redux/AppSlice";

const initialValues = {
  customerId: "",
  ticketId: "",
};

const validationSchema = object({
  customerId: string().required("Customer Organisation is required"),
  ticketId: string().required("Ticket is required"),
});

const CloseOnBehalfTicket = () => {
  const [organisations, setOrganisations] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const formikRef = useRef();
  const fileInputRef = useRef();
  const dispatch = useDispatch();

  useEffect(() => {
    const preventDefault = (e) => {
      e.preventDefault();
    };

    window.addEventListener("dragover", preventDefault);
    window.addEventListener("drop", preventDefault);
    return () => {
      window.removeEventListener("dragover", preventDefault);
      window.removeEventListener("drop", preventDefault);
    };
  }, []);

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "--";
    return `AED ${parseFloat(amount).toFixed(2)}`;
  };

  // Fetch customer organisations on mount
  useEffect(() => {
    dispatch(showLoader());
    const params = { type: "CUSTOMER" };
    AppService.get("v1/organisation/keyValues", { params })
      .then((response) => {
        setOrganisations(response);
      })
      .catch((error) => {
        console.error("Error fetching organisations:", error);
        dispatch(showErroMessage("Failed to load customer organisations"));
      })
      .finally(() => {
        dispatch(dismissLoader());
      });
  }, [dispatch]);

  // Set screen title
  useEffect(() => {
    dispatch(updateScreenTitle("Close On Behalf Ticket"));
    return () => dispatch(updateScreenTitle(""));
  }, [dispatch]);

  // Handle organisation change - fetch customer tickets
  const handleOrganisationChange = (event, setFieldValue) => {
    const orgId = event.target.value;
    setFieldValue("customerId", orgId);
    setFieldValue("ticketId", ""); // Reset ticket selection
    setTickets([]); // Clear tickets
    setSelectedTicket(null); // Clear selected ticket

    if (orgId) {
      dispatch(showLoader());
      AppService.get(`v2/ticket/customer/${orgId}/open-tickets`)
        .then((response) => {
          // Handle different response formats
          const ticketList = Array.isArray(response) 
            ? response 
            : (response?.content || response?.data || []);
          setTickets(ticketList);
        })
        .catch((error) => {
          console.error("Error fetching tickets:", error);
          dispatch(showErroMessage("Failed to load tickets"));
        })
        .finally(() => {
          dispatch(dismissLoader());
        });
    }
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      const newFiles = files.map((file) => ({
        file,
        name: file.name,
        size: file.size,
        id: Date.now() + Math.random(),
      }));
      setUploadedFiles((prev) => [...prev, ...newFiles]);
    }
    // Reset input so same file can be selected again
    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const files = Array.from(event.dataTransfer?.files || []);
    if (files.length === 0) return;

    const newFiles = files.map((file) => ({
      file,
      name: file.name,
      size: file.size,
      id: Date.now() + Math.random(),
    }));
    setUploadedFiles((prev) => [...prev, ...newFiles]);
  };

  // Handle file removal
  const handleFileRemove = (fileId) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Handle form submission
  const handleSubmit = async (values, { resetForm }) => {
    if (isSubmitting) {
      console.warn("Submission already in progress");
      return;
    }

    setIsSubmitting(true);
    dispatch(showLoader());

    try {
      // If file is uploaded, send as multipart form data
      if (uploadedFiles.length > 0) {
        const formData = new FormData();
        uploadedFiles.forEach((f) => {
          if (f?.file) formData.append("files", f.file);
        });
        
        await AppService.put(`v2/ticket/close-on-behalf-ticket/${values.ticketId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        // No file, send simple PUT request
        await AppService.put(`v2/ticket/close-on-behalf-ticket/${values.ticketId}`);
      }

      resetForm();
      setUploadedFiles([]);
      setTickets([]);
      setSelectedTicket(null);
      dispatch(showSuccessMessage("Ticket closed successfully!"));
    } catch (error) {
      console.error("Error closing ticket:", error);
      dispatch(showErroMessage(error?.response?.data?.message || "Failed to close ticket"));
    } finally {
      setIsSubmitting(false);
      dispatch(dismissLoader());
    }
  };

  return (
    <WDBox py={1}>
      <Card sx={{ padding: 3 }}>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
          innerRef={formikRef}
        >
          {({ errors, isValid, touched, dirty, setFieldValue, values }) => (
            <Form>
              <WDBox>
                <WDTypography fontSize={14} fontWeight="medium">
                  Close On Behalf Ticket
                </WDTypography>
              </WDBox>

              <WDBox my={3}>
                <Grid container spacing={3}>
                  {/* Customer Organisation Selection */}
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel id="customer-label">Customer Organisation</InputLabel>
                      <Field
                        name="customerId"
                        label="Customer Organisation"
                        labelId="customer-label"
                        size="small"
                        fullWidth
                        as={Select}
                        onChange={(e) => handleOrganisationChange(e, setFieldValue)}
                        error={Boolean(errors.customerId) && Boolean(touched.customerId)}
                      >
                        {organisations.map((org) => (
                          <MenuItem key={org.id} value={org.id}>
                            {org.name}
                          </MenuItem>
                        ))}
                      </Field>
                      {touched.customerId && errors.customerId && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                          {errors.customerId}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Ticket Selection */}
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel id="ticket-label">Select Ticket</InputLabel>
                      <Field
                        name="ticketId"
                        label="Select Ticket"
                        labelId="ticket-label"
                        size="small"
                        fullWidth
                        disabled={!values.customerId}
                        as={Select}
                        error={Boolean(errors.ticketId) && Boolean(touched.ticketId)}
                        onChange={(e) => {
                          const ticketId = e.target.value;
                          setFieldValue("ticketId", ticketId);
                          const ticket = tickets.find(t => t.id === ticketId);
                          setSelectedTicket(ticket || null);
                        }}
                      >
                        {tickets.map((ticket) => (
                          <MenuItem key={ticket.id} value={ticket.id}>
                            {ticket.orgTicketNumber || ticket.ticketNumber || `Ticket #${ticket.id}`}
                            {ticket.serviceName && ` - ${ticket.serviceName}`}
                          </MenuItem>
                        ))}
                      </Field>
                      {touched.ticketId && errors.ticketId && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                          {errors.ticketId}
                        </Typography>
                      )}
                      {values.customerId && tickets.length === 0 && (
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                          No tickets found for this customer
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Selected Ticket Details */}
                  {selectedTicket && (
                    <Grid item xs={12}>
                      <Paper 
                        elevation={0} 
                        sx={{ 
                          p: 2.5, 
                          backgroundColor: "#f8f9fa", 
                          border: "1px solid #e0e0e0",
                          borderRadius: 2,
                        }}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                          <Typography variant="subtitle1" fontWeight="600" color="primary">
                            Ticket Details
                          </Typography>
                          <Chip 
                            label={selectedTicket.status} 
                            size="small" 
                            color={selectedTicket.status === "OPEN" ? "success" : "default"}
                            sx={{ fontWeight: 500 }}
                          />
                        </Box>
                        
                        <Divider sx={{ mb: 2 }} />
                        
                        <Grid container spacing={2}>
                          {/* Ticket Info */}
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ mb: 1.5 }}>
                              <Typography variant="caption" color="textSecondary" sx={{ display: "block", mb: 0.25 }}>
                                Ticket Number
                              </Typography>
                              <Typography variant="body2" fontWeight="600">
                                {selectedTicket.orgTicketNumber}
                              </Typography>
                            </Box>
                          </Grid>
                          
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ mb: 1.5 }}>
                              <Typography variant="caption" color="textSecondary" sx={{ display: "block", mb: 0.25 }}>
                                Customer Name
                              </Typography>
                              <Typography variant="body2" fontWeight="500">
                                {selectedTicket.customerName || "--"}
                              </Typography>
                            </Box>
                          </Grid>
                          
                          <Grid item xs={12}>
                            <Box sx={{ mb: 1.5 }}>
                              <Typography variant="caption" color="textSecondary" sx={{ display: "block", mb: 0.25 }}>
                                Service
                              </Typography>
                              <Typography variant="body2" fontWeight="500">
                                {selectedTicket.serviceName || "--"}
                              </Typography>
                            </Box>
                          </Grid>
                          
                          <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                          </Grid>
                          
                          {/* Charges Section */}
                          <Grid item xs={12}>
                            <Typography variant="caption" color="textSecondary" fontWeight="600" sx={{ mb: 1, display: "block" }}>
                              CHARGES & COSTS
                            </Typography>
                          </Grid>
                          
                          <Grid item xs={6} sm={4}>
                            <Box sx={{ 
                              p: 1.5, 
                              backgroundColor: "#fff", 
                              borderRadius: 1,
                              border: "1px solid #e8e8e8"
                            }}>
                              <Typography variant="caption" color="textSecondary" sx={{ display: "block", mb: 0.5 }}>
                                Service Charge
                              </Typography>
                              <Typography variant="body2" fontWeight="600" color="text.primary">
                                {formatCurrency(selectedTicket.serviceCharge)}
                              </Typography>
                            </Box>
                          </Grid>
                          
                          <Grid item xs={6} sm={4}>
                            <Box sx={{ 
                              p: 1.5, 
                              backgroundColor: "#fff", 
                              borderRadius: 1,
                              border: "1px solid #e8e8e8"
                            }}>
                              <Typography variant="caption" color="textSecondary" sx={{ display: "block", mb: 0.5 }}>
                                Government Fees
                              </Typography>
                              <Typography variant="body2" fontWeight="600" color="text.primary">
                                {formatCurrency(selectedTicket.governmentFees)}
                              </Typography>
                            </Box>
                          </Grid>
                          
                          <Grid item xs={12} sm={4}>
                            <Box sx={{ 
                              p: 1.5, 
                              backgroundColor: "#1a8a7e", 
                              borderRadius: 1,
                            }}>
                              <Typography variant="caption" sx={{ display: "block", mb: 0.5, color: "rgba(255,255,255,0.8)" }}>
                                Total Cost
                              </Typography>
                              <Typography variant="body2" fontWeight="700" sx={{ color: "#fff" }}>
                                {formatCurrency(selectedTicket.totalCost)}
                              </Typography>
                            </Box>
                          </Grid>
                          
                          {/* Payment Status */}
                          <Grid item xs={12}>
                            <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="caption" color="textSecondary">
                                Payment Status:
                              </Typography>
                              <Chip 
                                label={selectedTicket.paymentStatus || "--"} 
                                size="small" 
                                color={selectedTicket.paymentStatus === "PAID" ? "success" : selectedTicket.paymentStatus === "PENDING" ? "warning" : "default"}
                                variant="outlined"
                                sx={{ fontWeight: 500 }}
                              />
                            </Box>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </WDBox>

              {/* Document Upload Section */}
              <WDBox my={3}>
                <WDTypography fontSize={14} fontWeight="medium" mb={2}>
                  Upload Documents
                </WDTypography>

                {/* Upload Button */}
                <Box
                  sx={{
                    border: "2px dashed #ccc",
                    borderRadius: 2,
                    p: 3,
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderColor: "#1a8a7e",
                      backgroundColor: "rgba(26, 138, 126, 0.04)",
                    },
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                  }}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    style={{ display: "none" }}
                    multiple
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                  />
                  <CloudUploadIcon sx={{ fontSize: 48, color: "#1a8a7e", mb: 1 }} />
                  <Typography variant="body1" color="textSecondary">
                    Click to upload or drag and drop
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    PDF, DOC, DOCX, JPG, PNG (Max 10MB each)
                  </Typography>
                </Box>

                {/* Uploaded Files List */}
                {uploadedFiles.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Uploaded Files ({uploadedFiles.length})
                    </Typography>
                    {uploadedFiles.map((file) => (
                      <Paper
                        key={file.id}
                        sx={{
                          p: 1.5,
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          backgroundColor: "#f5f5f5",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <InsertDriveFileIcon sx={{ color: "#1a8a7e" }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {file.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {formatFileSize(file.size)}
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleFileRemove(file.id)}
                          sx={{ color: "#d32f2f" }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Paper>
                    ))}
                  </Box>
                )}
              </WDBox>

              {/* Submit Button */}
              <WDBox display="flex" justifyContent="flex-end" pt={3}>
                <WDButton
                  variant="gradient"
                  disabled={!values.customerId || !values.ticketId || isSubmitting}
                  type="submit"
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </WDButton>
              </WDBox>
            </Form>
          )}
        </Formik>
      </Card>
    </WDBox>
  );
};

export default CloseOnBehalfTicket;
